import * as THREE from "three";

/**
 * Procedural meshes for the dungeon room props: the mine cart (depot), the
 * faceted loot gems with floating value/weight labels, and the torch-bearing
 * hero token. World frame: one grid cell = CELL world units, floor at y = 0.
 *
 * All RULES live in src/game / @jpjj/solver-core — these builders only draw.
 */

export const CELL = 1; // one grid cell, world units

/* Loot value tiers → gem colour (low · mid · high), matching the 2D palette. */
export const TIER_COLORS = [0x7fb6ff, 0x6fd3c0, 0xf3c768];

const mat = (color: number, opts: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.7, metalness: 0.1, ...opts });

const M = {
  cartBody: mat(0x3a2616, { roughness: 0.85 }),
  cartTrim: mat(0x8a5e2c, { roughness: 0.6, metalness: 0.3 }),
  iron: mat(0x161009, { roughness: 0.6, metalness: 0.4 }),
  gold: mat(0xe7bd5c, { roughness: 0.34, metalness: 0.85, emissive: 0x3a2a08, emissiveIntensity: 0.5 }),
  goldDeep: mat(0xc79433, { roughness: 0.4, metalness: 0.8 }),
  robe: mat(0x2d2620, { roughness: 0.9 }),
  robeDark: mat(0x1a1510, { roughness: 0.95 }),
  skin: mat(0xe7c79a, { roughness: 0.7 }),
  torchWood: mat(0x6b4a22, { roughness: 0.8 }),
};

function cyl(rT: number, rB: number, h: number, m: THREE.Material, x = 0, y = 0, z = 0, seg = 16): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rT, rB, h, seg), m);
  mesh.position.set(x, y, z);
  return mesh;
}

function shadowed(g: THREE.Object3D): void {
  g.traverse((o) => { if (o instanceof THREE.Mesh) { o.castShadow = true; o.receiveShadow = true; } });
}

/* ---------------- the mine cart (depot) ---------------- */

/** The mine cart: a trapezoid iron bin heaped with gold, on two wheels. The
 *  whole group is tagged `isCart` so a pick ray can recognise it. */
export function buildCart(): THREE.Group {
  const g = new THREE.Group();
  const bin = new THREE.Group();
  // bin body (a slightly tapered box)
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.34, 0.46, 4), M.cartBody);
  body.rotation.y = Math.PI / 4;
  body.position.y = 0.36;
  body.scale.set(1.18, 1, 1);
  bin.add(body);
  // iron rim
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.04, 8, 4), M.cartTrim);
  rim.rotation.x = Math.PI / 2;
  rim.rotation.z = Math.PI / 4;
  rim.position.y = 0.58;
  rim.scale.set(1.18, 1, 1);
  bin.add(rim);
  // gold heap
  const heap = new THREE.Mesh(new THREE.SphereGeometry(0.34, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2), M.gold);
  heap.scale.set(1.15, 0.7, 1);
  heap.position.y = 0.58;
  bin.add(heap);
  for (const [hx, hz, r] of [[-0.16, 0.05, 0.09], [0.12, -0.08, 0.1], [0.02, 0.14, 0.08], [0.2, 0.1, 0.07]] as const) {
    const coin = cyl(r, r, 0.04, M.gold, hx, 0.66, hz, 12);
    coin.rotation.x = Math.PI / 2 + (Math.random() - 0.5) * 0.6;
    bin.add(coin);
  }
  g.add(bin);
  // wheels
  for (const sx of [-1, 1]) {
    const w = cyl(0.16, 0.16, 0.08, M.iron, sx * 0.38, 0.16, 0, 14);
    w.rotation.z = Math.PI / 2;
    g.add(w);
    const hub = cyl(0.05, 0.05, 0.1, M.cartTrim, sx * 0.38, 0.16, 0, 8);
    hub.rotation.z = Math.PI / 2;
    g.add(hub);
  }
  // axle
  const axle = cyl(0.03, 0.03, 0.8, M.iron, 0, 0.16, 0, 8);
  axle.rotation.z = Math.PI / 2;
  g.add(axle);
  shadowed(g);
  g.userData.isCart = true;
  return g;
}

/* ---------------- loot gems ---------------- */

const GEM_GEO = new THREE.OctahedronGeometry(1, 0);

/** A floating canvas label showing the prize value and weight pips — a sprite,
 *  so it always faces the camera while orbiting. */
function makeLabel(prize: number, weight: number, color: number): THREE.Sprite {
  const W = 128, H = 96;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;
  // rounded dark badge
  ctx.fillStyle = "rgba(12,8,4,0.82)";
  const r = 18;
  ctx.beginPath();
  ctx.moveTo(r, 6); ctx.lineTo(W - r, 6); ctx.arcTo(W - 6, 6, W - 6, r, r);
  ctx.lineTo(W - 6, H - 28); ctx.arcTo(W - 6, H - 22, W - r, H - 22, r);
  ctx.lineTo(r, H - 22); ctx.arcTo(6, H - 22, 6, H - 28, r);
  ctx.lineTo(6, r); ctx.arcTo(6, 6, r, 6, r); ctx.closePath();
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = `rgba(${(color >> 16) & 255},${(color >> 8) & 255},${color & 255},0.85)`;
  ctx.stroke();
  // prize number
  ctx.fillStyle = "#f3e3bd";
  ctx.font = "bold 52px Cinzel, Georgia, serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(prize), W / 2, 40);
  // weight pips
  const pips = Math.min(weight, 6);
  const gap = 15, span = (pips - 1) * gap;
  for (let i = 0; i < pips; i++) {
    ctx.beginPath();
    ctx.arc(W / 2 - span / 2 + i * gap, H - 12, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = "#e7bd5c";
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: true }));
  spr.scale.set(0.62, 0.46, 1);
  spr.userData.isLabel = true;
  return spr;
}

/**
 * A faceted gem token for a loot node, with a floating value/weight label.
 * `tier` (0..2) sets the colour. The whole group carries `lootIdx` so a pick
 * ray can map it back to a loot node. Origin at the cell centre, floor y = 0.
 */
export function buildGem(idx: number, prize: number, weight: number, tier: number): THREE.Group {
  const g = new THREE.Group();
  const color = TIER_COLORS[tier] ?? TIER_COLORS[1];
  const size = 0.2 + Math.min(weight, 6) * 0.022; // heavier loot reads bigger
  const gemMat = new THREE.MeshStandardMaterial({
    color, roughness: 0.18, metalness: 0.1,
    emissive: color, emissiveIntensity: 0.28, flatShading: true,
  });
  const gem = new THREE.Mesh(GEM_GEO, gemMat);
  gem.scale.setScalar(size);
  gem.position.y = 0.34;
  gem.castShadow = true;
  g.add(gem);
  // a soft pedestal glow disc on the floor
  const glow = new THREE.Mesh(
    new THREE.CircleGeometry(size * 1.7, 20),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.16, depthWrite: false }),
  );
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.012;
  g.add(glow);
  const label = makeLabel(prize, weight, color);
  label.position.set(0, 0.78, 0);
  g.add(label);
  g.userData.lootIdx = idx;
  g.userData.gem = gem;
  g.userData.spin = 0.4 + Math.random() * 0.3;
  return g;
}

/* ---------------- the hero ---------------- */

/** The torch-bearing hero token (a hooded figure). Holds a flickering torch;
 *  `userData.flame` / `glowLight` are animated by the Stage. */
export function buildHero(): THREE.Group {
  const g = new THREE.Group();
  // robe (a tapered cone) + hood
  const robe = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.26, 0.5, 12), M.robe);
  robe.position.y = 0.25;
  g.add(robe);
  const shoulders = new THREE.Mesh(new THREE.SphereGeometry(0.17, 12, 10), M.robe);
  shoulders.scale.set(1, 0.7, 1);
  shoulders.position.y = 0.5;
  g.add(shoulders);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.12, 14, 12), M.skin);
  head.position.y = 0.62;
  g.add(head);
  const hood = new THREE.Mesh(new THREE.SphereGeometry(0.16, 14, 12, 0, Math.PI * 2, 0, Math.PI * 0.6), M.robeDark);
  hood.position.y = 0.64;
  g.add(hood);
  // the torch arm + torch
  const torch = new THREE.Group();
  const stick = cyl(0.018, 0.022, 0.42, M.torchWood, 0, 0.2, 0, 8);
  torch.add(stick);
  const flame = new THREE.Mesh(
    new THREE.SphereGeometry(0.07, 12, 10),
    new THREE.MeshBasicMaterial({ color: 0xffce6e }),
  );
  flame.scale.set(1, 1.5, 1);
  flame.position.y = 0.44;
  torch.add(flame);
  torch.position.set(0.26, 0.36, 0.1);
  torch.rotation.z = 0.2;
  g.add(torch);
  const light = new THREE.PointLight(0xffb45e, 2.4, 4.5, 2);
  light.position.set(0.28, 0.82, 0.1);
  g.add(light);
  // carried glow (brightens when hauling)
  const carry = new THREE.PointLight(0xe7bd5c, 0, 2.2, 2);
  carry.position.set(0, 0.5, 0);
  g.add(carry);
  shadowed(g);
  g.userData.flame = flame;
  g.userData.torchLight = light;
  g.userData.carryLight = carry;
  return g;
}
